import { useCallback, useRef } from 'react'
import { sortBy } from 'lib/array/arrayUtils'
import { trackCommittedStateDropped } from 'tripPlanner/utils/chatAnalytics'
import { useEventSubscription } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import type { TripPlannerCommittedStateEntry } from 'tripPlanner/assistant/surfaceSnapshots/committedStateEntry'
import { isLive } from 'tripPlanner/assistant/surfaceSnapshots/expiry'

// Mirrors useSurfaceActivityAccumulator's own cap: svc-trip's compactOptionalArrayWithStrictCap
// *rejects* (rather than truncates) recentCommittedState over this count, so an unbounded
// accumulator here would 400 every subsequent snapshot publish rather than degrading gracefully.
// A multi-stop trip searching hotels/experiences/flights at every stop can genuinely cross this
// within one session -- the file header comment's original "a handful of keys" assumption doesn't
// hold at that scale.
const COMMITTED_STATE_ACCUMULATOR_MAX = 40

// Shared by the write-time cap and the read-time collect below -- both need the exact same
// isLive-based prune, and duplicating that loop would be the kind of copy that drifts silently if
// either one's edge-case handling ever needs to change (matches this file's own stated reasoning
// for centralising isLive in expiry.ts in the first place).
function pruneExpiredEntries(accumulator: Map<string, TripPlannerCommittedStateEntry>) {
  accumulator.forEach((entry, key) => {
    if (!isLive(entry.expiresAt)) accumulator.delete(key)
  })
}

// Unlike capAndTrackDrops's sibling in useSurfaceActivityAccumulator, this Map overwrites by key
// rather than growing by push, so there is no single "oldest" entry to slice from. Eviction goes
// by expiresAt instead, soonest-first -- consistent with sortCommittedState's own newest-expiring-
// first wire order. Prunes expired entries first so the reported drop count reflects live facts
// actually lost, not entries pruneExpiredEntries would have collected for free on the next read --
// retention is identical either way (an already-expired entry always sorts before any live one
// regardless of when it's removed), this only affects what gets counted as a real loss.
function capAccumulatorSize(accumulator: Map<string, TripPlannerCommittedStateEntry>) {
  pruneExpiredEntries(accumulator)
  const overflow = accumulator.size - COMMITTED_STATE_ACCUMULATOR_MAX
  if (overflow <= 0) return
  trackCommittedStateDropped(overflow)
  sortBy(Array.from(accumulator.values()), entry => Date.parse(entry.expiresAt), 'asc')
    .slice(0, overflow)
    .forEach(entry => accumulator.delete(entry.key))
}

/**
 * Implementation-detail hook for TripPlannerChatProvider's recentCommittedState accumulator --
 * not exported for outside use. A useRef-backed Map, not Context: the only writers are
 * dispatch() calls through the event bus (any descendant can already reach that), and the only
 * reader is TripPlannerChatProvider's own resolveTurnAuthority -- no scattered call site ever
 * needs direct access to the accumulator itself.
 *
 * A genuine sibling, not a generalisation, of useSurfaceActivityAccumulator: container is a Map
 * keyed by entry.key (not a plain array), read semantics are prune-and-retain (not drain), and
 * there is a real TTL/isLive check on every read. Wire order comes from sortCommittedState at
 * publish time, not push order.
 *
 * Per-chat domain state, so it gets its own `reset_chat_state` subscription -- matching how
 * useSurfaceActivityAccumulator does the same for its own domain state. Chat switch clears itself,
 * internally, via that subscription. Logout/user-switch/auth-downgrade do NOT dispatch
 * reset_chat_state (TripPlannerChatProvider.tsx's authScope effect is the separate path for that
 * transition), so clearCommittedState is also exposed below for the provider to call explicitly
 * from there -- same reasoning useSurfaceActivityAccumulator's own clearSurfaceActivity export
 * documents for that transition.
 *
 * Known, accepted nuance: `Map.set` on an existing key does not move that key's position in
 * iteration order -- a producer that overwrites a long-lived key (e.g. `search:hotel:stop:1`,
 * first pushed early in a session) keeps that key's original push-order slot even as its value
 * changes. This only matters if push order were used for eviction, which it isn't here -- BE's
 * own render-time cut, this accumulator's own read path, and its size cap below all key off
 * `expiresAt`/`isLive`, not position. Documented so a future producer relying on
 * recency-by-push-order doesn't get surprised.
 */
export function useCommittedStateAccumulator() {
  const accumulatorRef = useRef<Map<string, TripPlannerCommittedStateEntry>>(new Map())

  useEventSubscription('trip_planner_committed_state', event => {
    event.entries.forEach(entry => accumulatorRef.current.set(entry.key, entry))
    capAccumulatorSize(accumulatorRef.current)
  })

  const clearCommittedState = useCallback(() => {
    accumulatorRef.current.clear()
  }, [])

  useEventSubscription('reset_chat_state', clearCommittedState)

  // Critical lifecycle rule: a pushed entry must keep reappearing in every snapshot build until
  // its own expiresAt has actually passed -- there is no "clear on send" or "after N turns"
  // trigger. This is the one read path, so it both prunes (deletes) and collects in the same
  // pass: an expired entry is safe to actually delete here, since svc-trip will independently
  // treat the same entry as TTL-expired in its own stored copy the moment it stops appearing, per
  // its own isExpiredAt check at render time.
  const collectLiveCommittedStateEntries = useCallback((): Array<TripPlannerCommittedStateEntry> => {
    pruneExpiredEntries(accumulatorRef.current)
    return Array.from(accumulatorRef.current.values())
  }, [])

  return { collectLiveCommittedStateEntries, clearCommittedState }
}
